import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Dropdown, DropdownMenuItem, Stack, SwitchCheckbox, TextField, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

const STAKES_OPTIONS = ['LOW', 'MEDIUM', 'HIGH'];

const buildEditorState = (marking) => {
  return {
    enabled: Boolean(marking?.enabled),
    live: Boolean(marking?.live),
    stakes: marking?.stakes || 'MEDIUM',
    // The API stores 0..1; ops edit a percentage.
    sampleRatePercent: marking?.sampleRate === undefined ? '100' : `${parseFloat((marking.sampleRate * 100).toFixed(2))}`,
  };
};

const fieldLabelSx = {
  fontWeight: 500,
  fontSize: '11px',
  lineHeight: '14px',
  color: '#A8A49E',
};

const hintSx = {
  fontSize: '12px',
  lineHeight: '16px',
  color: '#6B6761',
};

const DomainGuideMarking = (props) => {
  const {
    domainGuideData,
    updateMarking,
  } = props;

  const [editorState, setEditorState] = React.useState(() => buildEditorState(domainGuideData.marking));
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveFailed, setSaveFailed] = React.useState(false);

  React.useEffect(() => {
    setEditorState(buildEditorState(domainGuideData.marking));
  }, [domainGuideData.marking]);

  const setEditorField = (field, value) => {
    setEditorState((currentState) => ({
      ...currentState,
      [field]: value,
    }));
  };

  const sampleRateNumber = Number(editorState.sampleRatePercent);
  const isSampleRateValid = editorState.sampleRatePercent.trim() !== '' &&
    Number.isFinite(sampleRateNumber) &&
    sampleRateNumber >= 0 &&
    sampleRateNumber <= 100;

  const saveMarking = async () => {
    if (!isSampleRateValid || isSaving) {
      return;
    }

    setIsSaving(true);
    setSaveFailed(false);

    const saved = await updateMarking({
      enabled: editorState.enabled,
      live: editorState.live,
      stakes: editorState.stakes,
      sampleRate: sampleRateNumber / 100,
    });

    setSaveFailed(!saved);
    setIsSaving(false);
  };

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #E8E4DE',
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      <Stack sx={{gap: 2, px: 3, py: 2}}>
        <Stack sx={{gap: '4px'}}>
          <Typography sx={fieldLabelSx}>
            <Translate expression='domainGuide.Detail.Marking.Title' />
          </Typography>
          <Typography sx={hintSx}>
            <Translate expression='domainGuide.Detail.Marking.Subtitle' />
          </Typography>
        </Stack>
        {
          !domainGuideData.rubric &&
          <Typography sx={{...hintSx, color: '#C4512C'}}>
            <Translate expression='domainGuide.Detail.Marking.RubricRequired' />
          </Typography>
        }
        <Stack sx={{gap: '4px'}}>
          <SwitchCheckbox
            label={translate('domainGuide.Detail.Marking.EnabledLabel')}
            value={editorState.enabled}
            onChange={(enabled) => setEditorField('enabled', enabled)}
          />
          <Typography sx={hintSx}>
            <Translate expression='domainGuide.Detail.Marking.EnabledHint' />
          </Typography>
        </Stack>
        <Stack sx={{gap: '4px'}}>
          <SwitchCheckbox
            label={translate('domainGuide.Detail.Marking.LiveLabel')}
            value={editorState.live}
            onChange={(live) => setEditorField('live', live)}
          />
          <Typography sx={hintSx}>
            <Translate expression='domainGuide.Detail.Marking.LiveHint' />
          </Typography>
        </Stack>
        <Stack
          direction={{xs: 'column', sm: 'row'}}
          sx={{gap: 2}}
        >
          <Stack sx={{gap: '8px', flex: 1, minWidth: 0}}>
            <Typography sx={fieldLabelSx}>
              <Translate expression='domainGuide.Detail.Marking.StakesLabel' />
            </Typography>
            <Dropdown
              value={editorState.stakes}
              onChange={(stakes) => setEditorField('stakes', stakes)}
            >
              {
                STAKES_OPTIONS.map((stakesOption) => (
                  <DropdownMenuItem
                    key={stakesOption}
                    value={stakesOption}
                  >
                    {translate(`domainGuide.Detail.Marking.StakesOptions.${stakesOption}`)}
                  </DropdownMenuItem>
                ))
              }
            </Dropdown>
          </Stack>
          <Stack sx={{gap: '8px', flex: 1, minWidth: 0}}>
            <Typography sx={fieldLabelSx}>
              <Translate expression='domainGuide.Detail.Marking.SampleRateLabel' />
            </Typography>
            <TextField
              fullWidth
              value={editorState.sampleRatePercent}
              onChange={(sampleRatePercent) => setEditorField('sampleRatePercent', sampleRatePercent)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />
            <Typography sx={hintSx}>
              <Translate
                expression={
                  isSampleRateValid ?
                    'domainGuide.Detail.Marking.SampleRateHint' :
                    'domainGuide.Detail.Marking.SampleRateInvalid'
                }
              />
            </Typography>
          </Stack>
        </Stack>
        <Stack direction='row' sx={{gap: 2, alignItems: 'center'}}>
          <Button
            disabled={!isSampleRateValid || isSaving}
            onClick={saveMarking}
            sx={{
              'alignSelf': 'flex-start',
              'px': '16px',
              'py': '8px',
              'borderRadius': '8px',
              'backgroundColor': '#DA7955',
              'boxShadow': 'none',
              'color': '#FFFFFF',
              'fontSize': '13px',
              'fontWeight': 700,
              'textTransform': 'none',
              '&:hover': {
                backgroundColor: '#C4512C',
                boxShadow: 'none',
              },
            }}
            variant='contained'
          >
            {
              isSaving ?
                translate('domainGuide.Detail.Marking.Saving') :
                translate('domainGuide.Detail.Marking.Save')
            }
          </Button>
          {
            saveFailed &&
            <Typography sx={{fontSize: '12px', color: '#D85D31'}}>
              <Translate expression='domainGuide.Detail.Marking.SaveFailed' />
            </Typography>
          }
        </Stack>
      </Stack>
    </Box>
  );
};

DomainGuideMarking.propTypes = {
  domainGuideData: PropTypes.object.isRequired,
  updateMarking: PropTypes.func.isRequired,
};

export default React.memo(DomainGuideMarking);
